import { FC } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { AnimatedNumberIncrement, Button, LottieAnimation, Modal } from 'src/components';
import { AnimationStageValues } from 'src/components/AnimatedNumberIncrement';
import { defaultAnimationOptions } from 'src/components/constants';
import { ProgressResponse, UserPoints } from 'src/api/me/types';
import { numberToSignedString } from 'src/utils/numbers';
import soundFile from 'src/apps/LearningSession/EndOfSession/SessionCompletedCelebration/points-update.mp3';
import * as S from 'src/apps/LearningSession/EndOfSession/SessionCompletedCelebration/styles';
import sessionCompletedAnimation from 'src/apps/LearningSession/EndOfSession/SessionCompletedCelebration/session-completed-animation.json';

const msgs = defineMessages({
  title: { id: 'eos_learn_sessionCompletedCelebration_title' },
  learnWords: { id: 'eos_learn_sessionCompletedCelebration_learnWordsPoints_title' },
  fullyLearned: { id: 'eos_learn_sessionCompletedCelebration_learnWordsFullyLearned_title' },
  startedLearning: { id: 'eos_learn_scenarioSummary_startedLearning' },
  pointsAbbr: { id: 'homeScreen_myScore_points_abbreviature_web' },
  viewSummary: { id: 'eos_learn_sessionCompletedCelebration_learnWordsPoints_CTA' },
});

type WordsPoints = Pick<UserPoints, 'learnablePoints'>;
type WordsProgress = Pick<
  ProgressResponse,
  'learnablesFullyGrownCount' | 'learnablesTouchedButNotFullyGrownCount'
>;

type EndOfSessionProps = {
  pointsBefore: WordsPoints;
  pointsAfter: WordsPoints;
  progressBefore: WordsProgress;
  progressAfter: WordsProgress;
  onClick: () => void;
  isOpen: boolean;
};

export const SessionCompletedCelebration: FC<EndOfSessionProps> = ({
  pointsBefore,
  pointsAfter,
  progressBefore,
  progressAfter,
  onClick,
  isOpen,
}) => {
  const intl = useIntl();

  const progress = [
    {
      id: 'fullyLearned',
      title: intl.formatMessage(msgs.fullyLearned),
      startValue: progressBefore.learnablesFullyGrownCount,
      endValue: progressAfter.learnablesFullyGrownCount,
    },
    {
      id: 'startedLearning',
      title: intl.formatMessage(msgs.startedLearning),
      startValue: progressBefore.learnablesTouchedButNotFullyGrownCount,
      endValue: progressAfter.learnablesTouchedButNotFullyGrownCount,
    },
  ];

  return (
    <Modal isOpen={isOpen} fullSize hideCloseButton>
      <S.ModalContent>
        {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
        <audio autoPlay preload="auto" src={soundFile} data-testid="session-completed-sound" />
        <S.Header>{intl.formatMessage(msgs.title)}</S.Header>
        <S.AnimationContainer data-testid="session-completed-animation">
          <LottieAnimation
            {...defaultAnimationOptions}
            animationData={sessionCompletedAnimation}
            loop
          />
        </S.AnimationContainer>
        <S.StatisticWrapper data-testid="widget-slide-points">
          <S.PointsTitle data-testid="widget-slide-points-title">
            {intl.formatMessage(msgs.learnWords)}
          </S.PointsTitle>
          <AnimatedNumberIncrement
            startValue={pointsBefore.learnablePoints}
            endValue={pointsAfter.learnablePoints}
            renderStartStage={({ startValue }: AnimationStageValues) => (
              <S.PointsValues data-testid="start-stage-points">
                {intl.formatMessage(msgs.pointsAbbr, { points: startValue })}
              </S.PointsValues>
            )}
            renderIncrementStage={({ startValue, incrementValue }: AnimationStageValues) => (
              <S.PointsValues data-testid="increment-stage-points">
                {intl.formatMessage(msgs.pointsAbbr, { points: startValue })}{' '}
                <S.IncrementValue>+{incrementValue}</S.IncrementValue>
              </S.PointsValues>
            )}
            renderEndStage={({ endValue }: AnimationStageValues) => (
              <S.PointsValues data-testid="end-stage-points">
                <S.AccentValue>
                  {intl.formatMessage(msgs.pointsAbbr, { points: endValue })}
                </S.AccentValue>
              </S.PointsValues>
            )}
          />
        </S.StatisticWrapper>

        <S.Divider />

        <S.WordsStatisticWrapper>
          {progress.map(({ id, title, startValue, endValue }) => (
            <S.WordsStatistic key={id} data-testid={`widget-slide-${id}`}>
              <S.WordsTitle data-testid={`widget-slide-${id}-title`}>{title}</S.WordsTitle>
              <AnimatedNumberIncrement
                startValue={startValue}
                endValue={endValue}
                disabled={startValue === endValue}
                renderStartStage={({ startValue }: AnimationStageValues) => (
                  <S.WordsValues data-testid={`start-stage-${id}`}>{startValue}</S.WordsValues>
                )}
                renderIncrementStage={({ startValue, incrementValue }: AnimationStageValues) => (
                  <S.WordsValues data-testid={`increment-stage-${id}`}>
                    {startValue}{' '}
                    <S.IncrementValue>{numberToSignedString(incrementValue)}</S.IncrementValue>
                  </S.WordsValues>
                )}
                renderEndStage={({ endValue }: AnimationStageValues) => (
                  <S.WordsValues data-testid={`end-stage-${id}`}>
                    <S.AccentValue>{endValue}</S.AccentValue>
                  </S.WordsValues>
                )}
              />
            </S.WordsStatistic>
          ))}
        </S.WordsStatisticWrapper>
        <Button buttonType="primary" buttonSize="medium" onClick={onClick}>
          {intl.formatMessage(msgs.viewSummary)}
        </Button>
      </S.ModalContent>
    </Modal>
  );
};
